"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Event = void 0;
const axios_1 = __importDefault(require("axios"));
const jwt_decode_1 = require("jwt-decode");
const query_string_1 = __importDefault(require("query-string"));
const displayable_error_1 = require("./displayable-error");
const event_emitter_1 = require("./event-emitter");
const is_axios_error_1 = require("./is-axios-error");
const simple_hash_1 = require("./simple-hash");
const SCOPES = ["openid", "offline_access"];
const STORE_KEY_CODE_VERIFIER = "codeVerifier";
const STORE_KEY_ACCESS_TOKEN = "accessToken";
const STORE_KEY_ACCESS_TOKEN_EXPIRY = "accessTokenExpiry";
const STORE_KEY_REFRESH_TOKEN = "refreshToken";
const STORE_KEY_USER = "user";
const STORE_KEY_ACTION_REQUESTED_AT = "actionRequestedAt";
// This prevents us from processing an auth redirect multiple times with the
// same initial URL
const STORE_KEY_PREV_INITIAL_URL = "prevInitialUrl";
const CODE_CHALLENGE_METHOD = "S256";
// If an access token is within this many milliseconds of expiring then we may
// choose to preemptively refresh it
const ACCESS_TOKEN_EXPIRY_BUFFER = 60000;
var Event;
(function (Event) {
    Event["User"] = "USER";
    Event["Error"] = "ERROR";
    Event["Initialized"] = "INITIALIZED";
    Event["ActionRequested"] = "ACTION_REQUESTED";
    Event["ActionComplete"] = "ACTION_COMPLETE";
    // This is triggered when the authenticator expects a user to be logged in but
    // the auth service says their refresh token is invalid.
    Event["SessionExpired"] = "SESSION_EXPIRED";
    // This is triggered when the authenticator receives an authorization code via
    // an an oauth redirect. The authenticator will attempt to exchange the
    // authorization code for an access token.
    Event["AuthorizationCodeReceived"] = "AUTHORIZATION_CODE_RECEIVED";
})(Event = exports.Event || (exports.Event = {}));
class Authenticator extends event_emitter_1.EventEmitter {
    constructor(config) {
        super();
        this.user = null;
        this.accessTokenRefreshRequest = null;
        this.state = "instantiated";
        /**
         * PRIVATE METHODS
         */
        this.getEndpoint = (endpoint) => typeof endpoint === "string" ? endpoint : endpoint[this.config.type];
        this.getUserFromAccessTokenPayload = (accessToken) => {
            return {
                id: accessToken.sub,
                email: accessToken.email,
                tenantId: accessToken.tenantId,
                roles: accessToken.roles || [],
                createdAt: accessToken.ins,
            };
        };
        // Pulls the cached user out of the store and into our local state
        this.hydrateUser = async () => {
            const user = await this.config.store.get(STORE_KEY_USER);
            await this.setUser(user);
            return user;
        };
        this.setUser = async (user) => {
            if ((0, simple_hash_1.simpleHash)(this.user) !== (0, simple_hash_1.simpleHash)(user)) {
                this.user = user;
                await this.config.store.set(STORE_KEY_USER, this.user);
                this.emitter.emit(Event.User, this.user);
            }
        };
        this.publishError = (error) => {
            this.emitter.emit(Event.Error, error);
        };
        this.resetCache = async () => {
            this.user = null;
            await Promise.all([
                this.config.store.remove(STORE_KEY_USER),
                this.config.store.remove(STORE_KEY_ACCESS_TOKEN),
                this.config.store.remove(STORE_KEY_ACCESS_TOKEN_EXPIRY),
                this.config.store.remove(STORE_KEY_REFRESH_TOKEN),
                this.config.store.remove(STORE_KEY_CODE_VERIFIER),
                this.config.store.remove(STORE_KEY_ACTION_REQUESTED_AT),
            ]);
        };
        /**
         * Parses responses from both the FusionAuth token endpoint and our API's token
         * endpoint. Saves the appropriate bits of the response in the provided store.
         * Constructs a user object from the response and returns it.
         */
        this.handleTokenResponse = async (res) => {
            switch (this.config.type) {
                // This branch handles responses from a standard OAuth 2.0 endpoint. The
                // response is expected to contain both the access token and the refresh
                // token.
                case "bearer": {
                    if (!("access_token" in res.data)) {
                        throw new Error("Invalid token response - missing access_token");
                    }
                    const accessToken = res.data.access_token;
                    const accessTokenPayload = (0, jwt_decode_1.jwtDecode)(accessToken);
                    const user = this.getUserFromAccessTokenPayload(accessTokenPayload);
                    await Promise.all([
                        this.config.store.set(STORE_KEY_ACCESS_TOKEN, accessToken),
                        this.config.store.set(STORE_KEY_ACCESS_TOKEN_EXPIRY, accessTokenPayload.exp * 1000),
                        this.config.store.set(STORE_KEY_REFRESH_TOKEN, res.data.refresh_token),
                        this.setUser(user),
                    ]);
                    return;
                }
                // This branch handles responses from a custom cookie-based token
                // endpoint. The response is expected to contain the access token payload.
                // The access token and refresh tokens should be set in http-only cookies
                // that JS does not have access to.
                case "cookie": {
                    if (!("sub" in res.data)) {
                        throw new Error("Invalid token response - missing sub");
                    }
                    const user = this.getUserFromAccessTokenPayload(res.data);
                    await Promise.all([
                        this.config.store.set(STORE_KEY_ACCESS_TOKEN_EXPIRY, res.data.exp * 1000),
                        this.setUser(user),
                    ]);
                    return;
                }
            }
        };
        this.prepareAuthUrl = async (action, options) => {
            await this.emitter.emit(Event.ActionRequested, action);
            await this.config.store.set(STORE_KEY_ACTION_REQUESTED_AT, Date.now());
            const endpoint = action === "register"
                ? this.config.registerEndpoint
                : this.config.authorizeEndpoint;
            const { codeVerifier, codeChallenge } = await this.config.generatePkceChallenge();
            await this.config.store.set(STORE_KEY_CODE_VERIFIER, codeVerifier);
            // NOTE: Do not change the parameters here without also updating the
            // corresponding authorized redirect URL for all affected applications in
            // FusionAuth.
            return `${endpoint}?${query_string_1.default.stringify(Object.assign({ client_id: this.config.clientId, redirect_uri: this.config.redirectUrl, scope: SCOPES.join(" "), response_type: "code", code_challenge: codeChallenge, code_challenge_method: CODE_CHALLENGE_METHOD, locale: this.config.locale }, options === null || options === void 0 ? void 0 : options.query), { sort: (a, b) => a.localeCompare(b) })}`;
        };
        this.startAuthFlow = async (action, options) => {
            await this.config.redirectTo(await this.prepareAuthUrl(action, options));
        };
        /**
         * This is called when the auth service redirects back to us in order to
         * finish the OAuth authorization code flow.
         */
        this.finishAuthFlow = async (code) => {
            const [codeVerifier, actionRequestedAt] = await Promise.all([
                this.config.store.get(STORE_KEY_CODE_VERIFIER),
                this.config.store.get(STORE_KEY_ACTION_REQUESTED_AT),
            ]);
            // Get a new access token and refresh token. This will invalidate any
            // existing refresh tokens.
            const res = await axios_1.default.post(this.getEndpoint(this.config.tokenEndpoint), query_string_1.default.stringify({
                auth_type: this.config.type,
                client_id: this.config.clientId,
                redirect_uri: this.config.redirectUrl,
                grant_type: "authorization_code",
                code,
                code_verifier: codeVerifier,
            }), { withCredentials: true });
            // Reset any previously cached auth data. This is the point of no return.
            await this.resetCache();
            // Put the appropriate bits of the token response in a local store and
            // update the user in local state.
            await this.handleTokenResponse(res);
            // If we have a user object by this point then we had a successful login
            const user = this.getUser();
            if (user) {
                // There is no way to tell from from a spec OAuth callback whether the
                // user registered a new account or used an existing one. We also can't
                // count on the user to have followed through with the action they
                // originally requested. For exammple they may have asked to create a new
                // account and then instead chosen "Already have an account? Sign in". To
                // determine which action the user took we compare the timestamp that they
                // started the auth flow with the timestamp that their account was
                // registered. This won't be accurate if the client has clock issues, but
                // it's good enough for our purposes.
                const action = actionRequestedAt && user.createdAt > parseInt(actionRequestedAt)
                    ? "register"
                    : "login";
                this.emitter.emit(Event.ActionComplete, action);
            }
        };
        /**
         * Detects if an error is the result of an authorization problem (as opposed
         * to a network problem, server problem, etc.)
         */
        this.isAuthenticationError = (error) => Boolean((0, is_axios_error_1.isAxiosError)(error) && error.response && error.response.status === 400);
        /**
         * Parses a URL for an authorization code. Only codes from URLs matching
         * config.redirectUrl will be returned.
         */
        this.getAuthorizationCodeFromUrl = (rawUrl) => {
            if (rawUrl) {
                const { url, query } = query_string_1.default.parseUrl(rawUrl);
                return url.replace(/\/?$/g, "") ===
                    this.config.redirectUrl.replace(/\/?$/g, "") &&
                    typeof query.code === "string"
                    ? query.code
                    : null;
            }
            else {
                return null;
            }
        };
        /**
         * PUBLIC METHODS
         */
        /**
         * This sets up listeners and loads cached auth info from the store. If the
         * app was opened via a deep link then the initial URL should be passed to
         * this function so that the authenticator can handle auth redirects.
         */
        this.initialize = (initialUrl) => {
            // Initialization may only occur once
            if (this.state !== "instantiated") {
                return;
            }
            this.state = "initializing";
            const onInitialized = () => {
                this.state = "initialized";
                this.emitter.emit(Event.Initialized, this.user);
            };
            const initializeWithoutAuthorizationCode = () => {
                this.shouldAttemptAccessTokenRefresh()
                    .then(async (shouldRefresh) => {
                    if (shouldRefresh) {
                        await this.refreshAccessToken();
                    }
                    // An access token refresh will hydrate the user, so if we aren't
                    // going to refresh the token then we have to hydrate the user here.
                    else {
                        await this.hydrateUser();
                    }
                })
                    .catch(this.publishError)
                    .finally(onInitialized);
            };
            const initializeWithAuthorizationCode = async (code) => {
                this.emitter.emit(Event.AuthorizationCodeReceived, undefined);
                const prevUser = this.getUser();
                await this.finishAuthFlow(code)
                    // If we fail to initialize with a code, try initializing without one.
                    // This covers the situation where the user has multiple auth pages
                    // open, finishes signing in with one, then attempts to sign in again
                    // with the other. In that case the second authentication attempt will
                    // fail but we'll still be able to recover the pre-existing session.
                    .catch(initializeWithoutAuthorizationCode)
                    .catch((error) => {
                    this.publishError(new displayable_error_1.DisplayableError("An error occurred while signing in. Please try again.", error));
                    // If we lost a pre-existing user by failing to finish the auth flow
                    // then notify everyone that the user is now null
                    if (prevUser) {
                        this.emitter.emit(Event.User, null);
                    }
                })
                    .finally(onInitialized);
            };
            if (this.config.addDeepLinkListener) {
                this.config.addDeepLinkListener((deepLinkUrl) => {
                    const code = this.getAuthorizationCodeFromUrl(deepLinkUrl);
                    if (code) {
                        initializeWithAuthorizationCode(code);
                    }
                });
            }
            const authorizationCodeFromInitialUrl = this.getAuthorizationCodeFromUrl(initialUrl);
            if (authorizationCodeFromInitialUrl) {
                this.config.store
                    .get(STORE_KEY_PREV_INITIAL_URL)
                    .then((prevInitialUrl) => {
                    if (initialUrl !== prevInitialUrl) {
                        this.config.store.set(STORE_KEY_PREV_INITIAL_URL, initialUrl);
                        initializeWithAuthorizationCode(authorizationCodeFromInitialUrl);
                    }
                    else {
                        initializeWithoutAuthorizationCode();
                    }
                });
            }
            else {
                initializeWithoutAuthorizationCode();
            }
        };
        /**
         * Indicates whether a token refresh can be attempted. A return value of false
         * means there's no way a refresh will succeed and the client should move on.
         * A return value of true means that a refresh might work, but it's not
         * guaranteed.
         */
        this.canAttemptAccessTokenRefresh = async () => {
            // Allow the client to attempt an access token refresh if:
            // - We have a user object. If we have a user object then we've essentially
            //   told the client that a user is signed in. In that case we should allow
            //   the client to attempt a token refresh _even if we know it will fail_
            //   (i.e. even if we're in bearer mode and don't have a refresh token).
            //   This simplifies the client's logic because it doesn't have to
            //   understand the difference between a user with an invalid refresh token
            //   and a user that lost their refresh token - it can use the same token
            //   refresh flow for both.
            // - We're in cookie mode. We can't check for the presence of auth cookies,
            //   so there's always the chance that a refresh cookie exists.
            // - We're in bearer mode and we have a refresh token.
            return (this.user !== null ||
                this.config.type === "cookie" ||
                (this.config.type === "bearer" &&
                    Boolean(await this.config.store.get(STORE_KEY_REFRESH_TOKEN))));
        };
        /**
         * Indicates whether or not we should attempt to refresh the access token.
         * This will return true if canAttemptAccessTokenRefresh returns true _and_ we
         * we know we have either an expired access token or one that is within
         * ACCESS_TOKEN_EXPIRY_BUFFER milliseconds of expiring.
         */
        this.shouldAttemptAccessTokenRefresh = async () => {
            const [canRefresh, accessTokenExpiry] = await Promise.all([
                this.canAttemptAccessTokenRefresh(),
                this.config.store.get(STORE_KEY_ACCESS_TOKEN_EXPIRY),
            ]);
            return (canRefresh &&
                accessTokenExpiry !== null &&
                accessTokenExpiry <= Date.now() + ACCESS_TOKEN_EXPIRY_BUFFER);
        };
        /**
         * Get a new access token using the cached refresh token.
         */
        this.refreshAccessToken = async () => {
            const refresh = async () => {
                try {
                    // Clear out the access token expiry value as one way or another it'll
                    // be inavlid after this request. This also ensures that our auth error
                    // recover code below can easily tell if another instance of the
                    // authenticator updates this value.
                    await this.config.store.remove(STORE_KEY_ACCESS_TOKEN_EXPIRY);
                    let res;
                    switch (this.config.type) {
                        case "cookie": {
                            res = await axios_1.default.post(this.getEndpoint(this.config.tokenEndpoint), query_string_1.default.stringify({
                                auth_type: "cookie",
                                grant_type: "refresh_token",
                                client_id: this.config.clientId,
                            }), { withCredentials: true });
                            break;
                        }
                        case "bearer": {
                            res = await axios_1.default.post(this.getEndpoint(this.config.tokenEndpoint), query_string_1.default.stringify({
                                auth_type: "bearer",
                                grant_type: "refresh_token",
                                client_id: this.config.clientId,
                                refresh_token: await this.config.store.get(STORE_KEY_REFRESH_TOKEN),
                            }));
                            break;
                        }
                    }
                    await this.handleTokenResponse(res);
                }
                catch (error) {
                    // If we got an authentication error back then we're likely in one of
                    // the following situations:
                    //
                    // - a. Our refresh token expired
                    // - b. Our session was purposefully revoked (user deleted their account
                    //      on another device, admin locked the user, etc.)
                    // - c. Cookies or store items were cleared or corrupted (fairly
                    //      unlikely though - they would have to be cleared after the
                    //      refresh request was initiated, partially cleared, or corrupted).
                    // - d. Another refresh attempt using the same refresh token succeeded
                    //      before ours. This _should_ only happen on the web where the app
                    //      can be opened in lots of tabs that share the same cookies/store,
                    //      but the world is a wild and wonderful place so we assume this
                    //      could happen on native as well.
                    //
                    // The appropriate response to situations a-c is to consider the user's
                    // session as expired. The appropriate response to situation d is to
                    // poll the STORE_KEY_ACCESS_TOKEN_EXPIRY store variable for a couple
                    // seconds to see if it gets updated to some value in the future (in
                    // which case we know someone else successfully refreshed the token). We
                    // can't easily tell which situation we're in, so to be on the safe side
                    // we assume it's situation d and spend a couple seconds waiting to see
                    // if the access token gets updated by another instance. If we're right
                    // then we save the user's session. If we're wrong then we make the user
                    // wait for a couple seconds before booting them out. A worthy tradeoff.
                    if (this.isAuthenticationError(error)) {
                        try {
                            await new Promise((resolve, reject) => {
                                // Check for a new access token every 150ms up to 10 times for a
                                // total wait of 1.5 seconds.
                                const checkInterval = 150;
                                let checksRemaining = 10;
                                const interval = setInterval(async () => {
                                    // If the access token expiry store variable gets updated to a
                                    // new value in the future then we're good, pull the latest user
                                    // data out of the store and carry on.
                                    const currentAccessTokenExpiry = await this.config.store.get(STORE_KEY_ACCESS_TOKEN_EXPIRY);
                                    if (currentAccessTokenExpiry &&
                                        currentAccessTokenExpiry > Date.now()) {
                                        clearInterval(interval);
                                        await this.hydrateUser();
                                        resolve();
                                    }
                                    // Otherwise we've probably got an irrevocable expired session,
                                    // give up.
                                    else if (checksRemaining <= 0) {
                                        clearInterval(interval);
                                        reject();
                                    }
                                    checksRemaining -= 1;
                                }, checkInterval);
                            });
                        }
                        catch (_recoveryError) {
                            // If our user property is set then a user was logged in (although
                            // our failure to get a new access token means they aren't logged in
                            // anymore)
                            const wasLoggedIn = Boolean(this.user);
                            // If a user still exists in the store then the situation is mostly
                            // likely an expired session. If we don't have a user in the store
                            // then they probably logged out in another tab.
                            const sessionExpired = (await this.config.store.get(STORE_KEY_USER)) !== null;
                            // If we failed to refresh the token and couldn't recover from the
                            // failure then we'll want to clear the local cache and any cookies
                            // hanging around. This'll prevent invalid tokens from hanging
                            // around and being sent to the API, likely triggering 401s.
                            await this.resetCache();
                            if (this.config.type === "cookie") {
                                await axios_1.default.delete(this.getEndpoint(this.config.revokeTokenEndpoint), { withCredentials: true });
                            }
                            // If the user was logged in then notify everyone that they no longer
                            // are
                            if (wasLoggedIn) {
                                this.emitter.emit(Event.User, null);
                            }
                            // If the user's session expired then notify everyone
                            if (sessionExpired) {
                                this.emitter.emit(Event.ActionComplete, "logout");
                                this.emitter.emit(Event.SessionExpired, error);
                            }
                            // Re-throw the original error for the caller to handle
                            throw error;
                        }
                    }
                    // If we got some other error then we don't really know the status of
                    // the user's authentication. The best we can do is pull the last cached
                    // user data out of the store and leave it to the client to decide what
                    // to do with the error.
                    else {
                        await this.hydrateUser();
                        // Re-throw the original error for the caller to handle
                        throw error;
                    }
                }
                finally {
                    this.accessTokenRefreshRequest = null;
                }
            };
            // If we don't already have a request in-flight then send one
            if (!this.accessTokenRefreshRequest) {
                this.accessTokenRefreshRequest = refresh();
            }
            // Return a promise for the in-flight refresh request
            return this.accessTokenRefreshRequest;
        };
        /**
         * A nicer way to call startAuthFlow for the login endpoint.
         */
        this.login = (options) => this.startAuthFlow("login", options).catch((error) => {
            this.publishError(new displayable_error_1.DisplayableError("An error occurred. Please try again.", error));
        });
        /**
         * A nicer way to call startAuthFlow with the registerFirst setting on.
         */
        this.register = (options) => this.startAuthFlow("register", options).catch((error) => {
            this.publishError(new displayable_error_1.DisplayableError("An error occurred. Please try again.", error));
        });
        /**
         * This clears local auth state and attempts to revoke the current refresh
         * token.
         */
        this.logout = async () => {
            try {
                await this.emitter.emit(Event.ActionRequested, "logout");
                // Revoke the refresh token and clear cookies
                switch (this.config.type) {
                    // If we're using cookies then it's important that this request succeed,
                    // otherwise the user will remain logged in (we can't modify http-only
                    // cookies locally).
                    case "cookie": {
                        await axios_1.default.delete(this.getEndpoint(this.config.revokeTokenEndpoint), { withCredentials: true });
                        break;
                    }
                    // If we're not using cookies we can try to revoke the token, but if the
                    // request fails we can still safely log the user out of the device by
                    // resetting the cache (and blowing away the refresh token locally).
                    case "bearer": {
                        try {
                            await axios_1.default.delete(this.getEndpoint(this.config.revokeTokenEndpoint), {
                                headers: {
                                    "Content-Type": "application/x-www-form-urlencoded",
                                },
                                data: query_string_1.default.stringify({
                                    token: await this.config.store.get(STORE_KEY_REFRESH_TOKEN),
                                }),
                            });
                        }
                        catch (error) {
                            this.publishError(error);
                        }
                        break;
                    }
                }
                await this.resetCache();
                await Promise.all([
                    this.emitter.emit(Event.User, null),
                    this.emitter.emit(Event.ActionComplete, "logout"),
                ]);
                // Redirect to the logout endpoint to clean up the SSO session and then
                // redirect back to the login page
                const loginUrl = await this.prepareAuthUrl("login");
                await this.config.redirectTo(`${this.config.logoutEndpoint}?client_id=${this.config.clientId}&tenantId=${this.config.tenantId}&post_logout_redirect_uri=${encodeURIComponent(loginUrl)}`);
            }
            catch (error) {
                this.publishError(new displayable_error_1.DisplayableError(`An error occurred while signing out. Please try again${this.config.type === "cookie" ? " or clear your cookies" : ""}.`, error));
            }
        };
        this.setType = (type) => {
            this.config.type = type;
        };
        this.setLocale = (locale) => {
            this.config.locale = locale;
        };
        /**
         * Public getters
         */
        this.getUser = () => this.user;
        this.getAccessToken = () => this.config.store.get(STORE_KEY_ACCESS_TOKEN);
        this.getInitialized = () => this.state === "initialized";
        this.getType = () => this.config.type;
        this.config = config;
    }
}
exports.default = Authenticator;
